/**
 * Clones an array.
 * This is about 95% faster than an `Object.assign` or destructuring calls.
 * @param arr {Array}
 * @returns Array
 */
export const cloneArray = (arr: any[]) => {
  const newArray = []
  for (let i = 0; i < arr.length; i++) {
    newArray[i] = arr[i]
  }
  return newArray
}

/**
 * Coerces a value to an array.
 *
 * @param val - The value to coerce to an array.
 * @returns The coerced array.
 */
export const coerceToArray = (val: any): any[] => {
  if (!val) {
    return []
  } else if (Array.isArray(val)) {
    return val
  }
  return [val]
}

/**
 * This function should only be used for ordered arrays where the overlap is
 * only expected between the final elements of arr1 and first elements of arr2
 * @param arr1 {Array}
 * @param arr2 {Array}
 * @returns any[]
 */
export const combineArraysWhereOverlapMayOccur = (
  arr1: any[],
  arr2: any[],
): any[] => {
  while (lastElIdAndFirstElIdAreEqual(arr1, arr2) && arr1.length > 0) {
    arr1.pop()
  }
  return arr1.concat(arr2)
}

/**
 * Flattens an array of arrays into a single-level array.
 *
 * @param {Array} [collector=[]] - The initial collector array to store the flattened array.
 * @param {Array} arr - The array of arrays to be flattened.
 * @returns {Array} - The flattened array.
 */
export const flattenArrayOfArrays = (collector: any[] = [], arr: any[]): any => {
  return Array.prototype.concat.apply(collector, coerceToArray(arr))
}

/**
 * Given two arrays of objects, returns whether the `id` value of the final
 * element in array 1 matches the `id` value of the first element in array 2
 * @param arr1 {Array}
 * @param arr2 {Array}
 * @returns boolean
 */
export const lastElIdAndFirstElIdAreEqual = (
  arr1: { [key: string]: any }[],
  arr2: { [key: string]: any }[],
): boolean => {
  return arr1?.at(arr1?.length - 1)?.id === arr2?.at(0)?.id
}


/**
 * Sorts an array of objects by multiple properties
 * @param arr
 * @param sortKeys
 * @param accessorKey
 */
export const inPlaceMultiFieldSort = (arr: any[], sortKeys: string[], accessorKey: string|undefined = undefined) => {
  arr.sort((a, b) => {
    for (const key of sortKeys) {
      const direction = key[0] === '-' ? -1 : 1;
      const field = key[0] === '-' ? key.slice(1) : key;

      const valueA = accessorKey ? (a[accessorKey]||{})[field] : a[field];
      const valueB = accessorKey ? (b[accessorKey]||{})[field] : b[field];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const comparison = valueA.localeCompare(valueB);
        if (comparison !== 0) {
          return direction * comparison;
        }
      } else if (valueA !== valueB) {
        return direction * (valueA > valueB ? 1 : -1);
      }
    }
    return 0;
  });
}